import { playlistActionTypes } from '@/playlist/Constants';

import type { CurrentLevel, EndOutcome, Question, SendAnswerRequestParams } from '@/playlist/types';

// ============================================================================
// Playlist Fetching
// ============================================================================

export function getPlaylistRequest(data: {
	id?: string | number;
	freePlayCategoryId?: string | number;
}) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REQUEST,
		payload: data,
	};
}

export function getPlaylistSuccess(data: any) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_SUCCESS,
		payload: { data },
	};
}

export function getPlaylistError() {
	return {
		type: playlistActionTypes.GET_PLAYLIST_ERROR,
	};
}

export function getPlaylistReportRequest(data: { id: string | number }) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPORT_REQUEST,
		payload: data,
	};
}

export function getPlaylistReportSuccess(data: any) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPORT_SUCCESS,
		payload: { data },
	};
}

export function getPlaylistReportError() {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPORT_ERROR,
	};
}

export function getPlaylistReplayRequest(data: { id: string | number }) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPLAY_REQUEST,
		payload: data,
	};
}

export function getPlaylistReplaySuccess(data: any) {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPLAY_SUCCESS,
		payload: { data },
	};
}

export function getPlaylistReplayError() {
	return {
		type: playlistActionTypes.GET_PLAYLIST_REPLAY_ERROR,
	};
}

// ============================================================================
// Question Explanations
// ============================================================================

export function getQuestionsExplanations({ questionIds }: { questionIds: number[] }) {
	return {
		type: playlistActionTypes.GET_QUESTIONS_EXPLANATIONS,
		payload: { questionIds },
	};
}

export function getQuestionsExplanationsSuccess(data: any) {
	return {
		type: playlistActionTypes.GET_QUESTIONS_EXPLANATIONS_SUCCESS,
		payload: data,
	};
}

// ============================================================================
// Answer Submission
// ============================================================================

export function sendAnswerRequest(params: SendAnswerRequestParams) {
	return {
		type: playlistActionTypes.SEND_ANSWER_REQUEST,
		payload: params,
	};
}

export function sendAnswerSuccess() {
	return {
		type: playlistActionTypes.SEND_ANSWER_SUCCESS,
	};
}

export function sendAnswerError() {
	return {
		type: playlistActionTypes.SEND_ANSWER_ERROR,
	};
}

// ============================================================================
// Playlist Completion
// ============================================================================

export function finishPlaylistRequest(
	playlistId: number | string,
	freePlayCategoryId?: number | string | null,
	gameOutcome?: EndOutcome,
	score?: number,
) {
	return {
		type: playlistActionTypes.FINISH_PLAYLIST_REQUEST,
		payload: { playlistId, freePlayCategoryId, gameOutcome, score },
	};
}

export function finishPlaylistSuccess() {
	return {
		type: playlistActionTypes.FINISH_PLAYLIST_SUCCESS,
	};
}

export function finishPlaylistError() {
	return {
		type: playlistActionTypes.FINISH_PLAYLIST_ERROR,
	};
}

// ============================================================================
// State Management
// ============================================================================

export function clearState() {
	return {
		type: playlistActionTypes.CLEAR_STATE,
	};
}

export function setExerciseScore(
	correctCount: number,
	incorrectCount: number,
	questionsLeftCount: number,
) {
	return {
		type: playlistActionTypes.SET_EXERCISE_SCORE,
		payload: { correctCount, incorrectCount, questionsLeftCount },
	};
}

export function setCurrentQuestion(currentQuestion: Question) {
	return {
		type: playlistActionTypes.SET_CURRENT_QUESTION,
		payload: { currentQuestion },
	};
}

export function setViewMode(viewMode: string) {
	return {
		type: playlistActionTypes.SET_VIEW_MODE,
		payload: { viewMode },
	};
}

export function setSkipAnswerRequests(skipAnswerRequests: boolean) {
	return {
		type: playlistActionTypes.SET_SKIP_ANSWER_REQUESTS,
		payload: { skipAnswerRequests },
	};
}

export function setSkipFinishRequest(skipFinishRequest: boolean) {
	return {
		type: playlistActionTypes.SET_SKIP_FINISH_REQUEST,
		payload: { skipFinishRequest },
	};
}

export function setIsBridgePlaylist(isBridgePlaylist: boolean) {
	return {
		type: playlistActionTypes.SET_IS_BRIDGE_PLAYLIST,
		payload: { isBridgePlaylist },
	};
}

export function setIsPersonalityQuiz(isPersonalityQuiz: boolean) {
	return {
		type: playlistActionTypes.SET_IS_PERSONALITY_QUIZ,
		payload: { isPersonalityQuiz },
	};
}

export function addPersonalityQuizAnswerId(answerId: number) {
	return {
		type: playlistActionTypes.ADD_PERSONALITY_QUIZ_ANSWER_ID,
		payload: { answerId },
	};
}

// ============================================================================
// Exercise Types
// ============================================================================

export function handleNextExerciseTypes(exerciseType: string) {
	return {
		type: playlistActionTypes.HANDLE_NEXT_EXERCISE_TYPES,
		payload: { exerciseType },
	};
}

// ============================================================================
// Scoring
// ============================================================================

export function setCorrectStreak(correctStreak: number) {
	return {
		type: playlistActionTypes.SET_CORRECT_STREAK,
		payload: { correctStreak },
	};
}

// ============================================================================
// Instructions
// ============================================================================

export function toggleTopicInstruction(showTopicInstruction: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_TOPIC_INSTRUCTION,
		payload: { showTopicInstruction },
	};
}

export function toggleQuestionInstruction(showQuestionInstruction: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_QUESTION_INSTRUCTION,
		payload: { showQuestionInstruction },
	};
}

export function setShowInstructionHint(showInstructionHint: boolean) {
	return {
		type: playlistActionTypes.SET_SHOW_INSTRUCTION_HINT,
		payload: { showInstructionHint },
	};
}

export function sendTopicInstructionFeedback(
	isUseful: boolean,
	instructionId: number | string,
	questionId: number | string,
) {
	return {
		type: playlistActionTypes.SEND_TOPIC_INSTRUCTION_FEEDBACK,
		payload: { isUseful, instructionId, questionId },
	};
}

export function sendQuestionInstructionFeedback(feedback: string, questionId: number | string) {
	return {
		type: playlistActionTypes.SEND_QUESTION_INSTRUCTION_FEEDBACK,
		payload: { feedback, questionId },
	};
}

// ============================================================================
// Audio
// ============================================================================

export function toggleInstructionAudio(audioOn: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_INSTRUCTION_AUDIO,
		payload: { audioOn },
	};
}

export function setInstructionAudioOn(instructionAudioOn: boolean) {
	return {
		type: playlistActionTypes.SET_INSTRUCTION_AUDIO_ON,
		payload: { instructionAudioOn },
	};
}

export function saveAudioPreferencesRequest(audioOn: boolean, audioOnboarded: boolean) {
	return {
		type: playlistActionTypes.SAVE_AUDIO_PREFERENCES_REQUEST,
		payload: { audioOn, audioOnboarded },
	};
}

export function saveAudioPreferencesSuccess() {
	return {
		type: playlistActionTypes.SAVE_AUDIO_PREFERENCES_SUCCESS,
	};
}

export function saveAudioPreferencesError() {
	return {
		type: playlistActionTypes.SAVE_AUDIO_PREFERENCES_ERROR,
	};
}

// ============================================================================
// Reading Mode
// ============================================================================

export function toggleReadingMode(isReadingMode: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_READING_MODE,
		payload: { isReadingMode },
	};
}

export function toggleReadingTitle(showReadingTitle: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_READING_TITLE,
		payload: { showReadingTitle },
	};
}

export function togglePulsatingHighlight(showPulsatingHighlight: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_PULSATING_HIGHLIGHT,
		payload: { showPulsatingHighlight },
	};
}

export function setReadingProgress(readingProgress: number) {
	return {
		type: playlistActionTypes.SET_READING_PROGRESS,
		payload: { readingProgress },
	};
}

export function setReadingTextSize(readingTextSize: number) {
	return {
		type: playlistActionTypes.SET_READING_TEXT_SIZE,
		payload: { readingTextSize },
	};
}

// ============================================================================
// Time Tracking
// ============================================================================

export function storeUserQuestionTime(answerId: number | string, playtimeInSeconds: number) {
	return {
		type: playlistActionTypes.STORE_USER_QUESTION_TIME,
		payload: { answer_id: answerId, playtime_in_seconds: playtimeInSeconds },
	};
}

// ============================================================================
// Completion States
// ============================================================================

export function setEndscreen(endscreen: any) {
	return {
		type: playlistActionTypes.SET_ENDSCREEN,
		payload: { endscreen },
	};
}

export function setLevelUp(levelUp: any) {
	return {
		type: playlistActionTypes.SET_LEVEL_UP,
		payload: { levelUp },
	};
}

// ============================================================================
// Mistake Intervention
// ============================================================================

export function setMistakeIntervention(mistakeInterventionId: number | null) {
	return {
		type: playlistActionTypes.SET_MISTAKE_INTERVENTION,
		payload: { mistakeInterventionId },
	};
}

export function sendMistakeInterventionRequest(
	playlistId: number | string,
	mistakeInterventionId: number | string,
) {
	return {
		type: playlistActionTypes.SEND_MISTAKE_INTERVENTION_REQUEST,
		payload: { playlistId, mistakeInterventionId },
	};
}

export function sendMistakeInterventionSuccess() {
	return {
		type: playlistActionTypes.SEND_MISTAKE_INTERVENTION_SUCCESS,
	};
}

export function sendMistakeInterventionError() {
	return {
		type: playlistActionTypes.SEND_MISTAKE_INTERVENTION_ERROR,
	};
}

export function resetMistakeInterventionRequest(mistakeInterventionId: number | string) {
	return {
		type: playlistActionTypes.RESET_MISTAKE_INTERVENTION_REQUEST,
		payload: { mistakeInterventionId },
	};
}

export function resetMistakeInterventionSuccess() {
	return {
		type: playlistActionTypes.RESET_MISTAKE_INTERVENTION_SUCCESS,
	};
}

export function resetMistakeInterventionError() {
	return {
		type: playlistActionTypes.RESET_MISTAKE_INTERVENTION_ERROR,
	};
}

// ============================================================================
// WebSocket
// ============================================================================

export function receivedLastActiveDeviceChannelStream(data: any) {
	return {
		type: playlistActionTypes.RECEIVED_LAST_ACTIVE_DEVICE_CHANNEL_STREAM,
		payload: { data },
	};
}

// ============================================================================
// AI Tutor
// ============================================================================

export function sendAiTutorFeedback(feedback: string, questionId: number | string) {
	return {
		type: playlistActionTypes.SEND_AI_TUTOR_FEEDBACK,
		payload: { feedback, questionId },
	};
}

export function toggleAiTutorFeedback(aiTutorFeedbackOpen: boolean) {
	return {
		type: playlistActionTypes.TOGGLE_AI_TUTOR_FEEDBACK,
		payload: { aiTutorFeedbackOpen },
	};
}

// ============================================================================
// Game-specific
// ============================================================================

export function setCurrentLevel(currentLevel: CurrentLevel) {
	return {
		type: playlistActionTypes.SET_CURRENT_LEVEL,
		payload: { currentLevel },
	};
}
